import React, { useState, useEffect } from 'react';
import { IoArrowForwardOutline, IoMegaphoneOutline } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import SubpageBanner from '../components/SubpageBanner';
import api from '../services/api';

export default function Promotions() {
    const { t } = useTranslation();
    const [promotionsData, setPromotionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format date function
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Create URL-friendly slug from title
    const createSlug = (title) => {
        // Turkish character mapping
        const turkishChars = {
            'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
            'Ç': 'c', 'Ğ': 'g', 'İ': 'i', 'Ö': 'o', 'Ş': 's', 'Ü': 'u'
        };

        return title
            .toLowerCase()
            .replace(/[çğıöşüÇĞİÖŞÜ]/g, (char) => turkishChars[char] || char) // Replace Turkish characters
            .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .trim();
    };

    // Get base URL for images
    const getImageBaseUrl = () => {
        const origin = window.location.origin;
        const isLocalOrTest = origin.toLowerCase().includes('local') || origin.toLowerCase().includes('test');
        return isLocalOrTest ? origin : origin;
    };

    // Fetch promotions data
    useEffect(() => {
        const fetchPromotions = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.getAllPromotions();
                if (response.success && response.data) {
                    setPromotionsData(response.data);
                } else {
                    setError(t('news.promotionError'));
                }
            } catch (err) {
                console.error('Error fetching promotions:', err);
                setError(t('news.promotionError'));
            } finally {
                setLoading(false);
            }
        };

        fetchPromotions();
    }, []);

    if (loading) {
        return (
            <>
                <SubpageBanner
                    title={t('news.promotionsTitle')}
                    page={t('news.promotionsPage')}
                    path="/promotions"
                />
                <div className='news-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('news.loading')}</span>
                                </div>
                                <p className="mt-3">{t('news.loadingPromotions')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <SubpageBanner
                    title={t('news.promotionsTitle')}
                    page={t('news.promotionsPage')}
                    path="/promotions"
                />
                <div className='news-content'>
                    <div className="container">
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                            <div className="text-center">
                                <div className="alert alert-danger">
                                    <h4>{t('news.error')}</h4>
                                    <p>{error}</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => window.location.reload()}
                                    >
                                        {t('news.retry')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SubpageBanner
                title={t('news.promotionsTitle')}
                page={t('news.promotionsPage')}
                path="/promotions"
            />
            <div className='promotions-content'>
                <div className='container'>
                    {promotionsData.length === 0 ? (
                        <div className='text-center py-5'>
                            <div className='empty-promotions-container'>
                                <div className='empty-promotions-icon mb-4'>
                                    <IoMegaphoneOutline size={64} style={{ color: 'var(--muted)' }} />
                                </div>
                                <h4 className='empty-promotions-title mb-3'>
                                    {t('news.noPromotions')}
                                </h4>
                                <p className='empty-promotions-text mb-0' style={{ color: 'var(--muted)' }}>
                                    {t('news.noPromotionsDescription')}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className='news-body'>
                            {promotionsData.map((item, index) => (
                                <a
                                    key={index}
                                    target='_self'
                                    rel=''
                                    href={`/promotions/${createSlug(item.title)}/${item.id}`}
                                    className='news-body-item d-flex align-items-center justify-content-between'
                                >
                                    <div className='d-flex align-items-center'>
                                        {item.imageUrl && (
                                            <div className='pe-4 promotion-image'>
                                                <img
                                                    src={`${getImageBaseUrl()}/api/cms/images${item.imageUrl}`}
                                                    alt={item.title}
                                                    style={{
                                                        width: '150px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className='ps-4'>
                                            <h5 className='mb-1'>
                                                {item.title}
                                            </h5>
                                            <span>
                                                {formatDate(item.startDate)}
                                            </span>
                                            {item.content && (
                                                <p className='mt-2 small' style={{ color: 'var(--muted)' }}>
                                                    {item.content}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className='d-flex align-items-center'>
                                        <div className='ms-4'>
                                            <IoArrowForwardOutline size={22} />
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
